import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { List, ListItem, Popover } from '@mui/material';
import css from './FiltersPopover.css';
import SearchTextField from '../SearchTextField/SearchTextField';
import { useOutsideEventHandler } from '../../../utils/customHooks';
import { IconSizes } from '../../foundations/Icon';

const FiltersPopover = ({
    open,
    position = null,
    onClose,
    filtersByCategory = {},
    selectedFilters = [],
    onFilterSelect,
    IconComponent = null,
}) => {
    const popoverRef = useRef(null);
    const [search, setSearch] = useState('');

    const categoriesWithFilters = useMemo(() => {
        let baseCategoriesWithFilters = Object.entries(filtersByCategory);
        if (selectedFilters.length) {
            const selectedFiltersSet = new Set(selectedFilters);
            baseCategoriesWithFilters = baseCategoriesWithFilters.map(([category, filters]) => [
                category,
                filters.filter(filter => !selectedFiltersSet.has(filter)),
            ]);
        }
        return baseCategoriesWithFilters.filter(([, filters]) => filters.length);
    }, [filtersByCategory, selectedFilters]);

    const filteredCategoriesWithFilters = useMemo(() => {
        if (!search) return categoriesWithFilters;

        return categoriesWithFilters.reduce((filtered, [category, filters]) => {
            const filteredFilters = filters.filter(filter => filter.toLowerCase().includes(search.toLowerCase()));

            if (filteredFilters.length) filtered.push([category, filteredFilters]);

            return filtered;
        }, []);
    }, [categoriesWithFilters, search]);

    useEffect(() => {
        if (!open) setSearch('');
    }, [open]);

    useOutsideEventHandler(popoverRef, onClose);

    if (!position) return null;

    return (
        <Popover
            className={css.filterPopover}
            open={open}
            anchorEl={position}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <div ref={popoverRef} className={css.popoverContent}>
                <SearchTextField search={search} onSearchChange={setSearch} iconSize={IconSizes.X_SMALL} clearable />
                <List className={css.filtersList}>
                    {filteredCategoriesWithFilters.map(([category, filters]) => (
                        <div key={category} className={css.categorySection}>
                            <div className={css.categoryTitle}>
                                {category.startsWith('STATIC.') ? <Translate id={category} /> : category}
                            </div>
                            {filters.map(filter => (
                                <ListItem
                                    key={filter}
                                    className={css.filterItem}
                                    onClick={() => onFilterSelect(filter)}
                                >
                                    <div className={css.filterListItemText}>
                                        {IconComponent && <IconComponent filter={filter} category={category} />}
                                        <div className={css.filterName}>{filter}</div>
                                    </div>
                                </ListItem>
                            ))}
                        </div>
                    ))}
                </List>
            </div>
        </Popover>
    );
};

FiltersPopover.propTypes = {
    open: PropTypes.bool.isRequired,
    position: PropTypes.instanceOf(Element),
    onClose: PropTypes.func.isRequired,
    filtersByCategory: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    selectedFilters: PropTypes.arrayOf(PropTypes.string),
    onFilterSelect: PropTypes.func.isRequired,
    IconComponent: PropTypes.func,
};

export default FiltersPopover;
